import { chooseDBTranslation } from 'utils/translations'
import { Paragraph, TextRun } from 'docx'
import { CVData } from 'types/printInterfaces'
import { IHideSetting } from 'types/layoutInterfaces'
import { isDetailHidden } from '../hideSettings'
import { itemHeading, itemHeadingTabStops, itemParagraphSpacing } from './itemHeading'
import { fontSize } from './pageLayout'
import { convertToDisplayDate } from 'utils/utils'
import { i18n as i18nType, TFunction } from 'i18next'

/**
 * Generates a list of paragraphs based on the provided CV data and type.
 *
 * @param {string} i18n - the internationalization object
 * @param {CVData} data - the CV data object
 * @param {string} type - the type of data to generate paragraphs for
 * @param {boolean} showCertificateExpiry - whether certificates show their expiry date
 * @param {function} t - the translation function
 * @param {IHideSetting | undefined} hideSetting - the hide setting of the education type
 * @return {Paragraph[]} an array of generated paragraphs
 * @notExported
 */
const education = (
  i18n: i18nType,
  data: CVData,
  type: string,
  showCertificateExpiry: boolean,
  t: TFunction,
  hideSetting?: IHideSetting
): Paragraph[] => {
  const items: Paragraph[] = []
  const hidden = (detail: string) => isDetailHidden(hideSetting, detail)

  switch (type) {
    case 'degrees':
      for (let i = 0; i < data.degrees.length; i++) {
        const degree = data.degrees[i]

        let date = ''
        if (degree.startDate) {
          date += `${convertToDisplayDate(degree.startDate, degree.dateResolution)} - `
        }
        if (degree.endDate) {
          date += `${convertToDisplayDate(degree.endDate, degree.dateResolution)}`
        }

        items.push(
          new Paragraph({
            keepLines: true,
            style: 'CaleoNormal',
            tabStops: itemHeadingTabStops,
            spacing: itemParagraphSpacing,
            children: [
              ...itemHeading(
                hidden('name') ? '' : chooseDBTranslation(i18n, degree).school,
                hidden('date') ? '' : date
              ),
              ...(hidden('degree')
                ? []
                : [
                    new TextRun({
                      text: `${t('print.degree')}: `,
                      bold: true,
                      break: 1,
                    }),
                    new TextRun({
                      text: chooseDBTranslation(i18n, degree).degree,
                    }),
                  ]),
              ...(hidden('description')
                ? []
                : [
                    new TextRun({
                      text: chooseDBTranslation(i18n, degree).description,
                      size: fontSize.small,
                      break: 1,
                    }),
                  ]),
            ],
          })
        )
      }
      break

    case 'courses':
      for (let i = 0; i < data.courses.length; i++) {
        const course = data.courses[i]
        let date = ''
        if (course.startDate) {
          date += `${convertToDisplayDate(course.startDate, course.dateResolution)} - `
        }
        if (course.endDate) {
          date += `${convertToDisplayDate(course.endDate, course.dateResolution)}`
        }

        items.push(
          new Paragraph({
            keepLines: true,
            style: 'CaleoNormal',
            tabStops: itemHeadingTabStops,
            spacing: itemParagraphSpacing,
            children: [
              ...itemHeading(
                hidden('name') ? '' : chooseDBTranslation(i18n, course).course,
                hidden('date') ? '' : date
              ),
              ...(hidden('organizer')
                ? []
                : [
                    new TextRun({
                      text: `${t('print.organizer')}: `,
                      bold: true,
                      break: 1,
                    }),
                    new TextRun({
                      text: chooseDBTranslation(i18n, course).organizer,
                    }),
                  ]),
              ...(hidden('description')
                ? []
                : [
                    new TextRun({
                      text: chooseDBTranslation(i18n, course).description,
                      size: fontSize.small,
                      break: 1,
                    }),
                  ]),
            ],
          })
        )
      }
      break

    case 'certificates':
      for (let i = 0; i < data.personCertificates.length; i++) {
        const certificate = data.personCertificates[i]
        let date = ''
        if (certificate.issueDate) {
          date += `${convertToDisplayDate(certificate.issueDate, certificate.dateResolution)}`
        }
        let endDate = ''
        if (certificate.endDate) {
          endDate += `${convertToDisplayDate(certificate.endDate, certificate.dateResolution)}`
        }

        items.push(
          new Paragraph({
            keepLines: true,
            style: 'CaleoNormal',
            tabStops: itemHeadingTabStops,
            spacing: itemParagraphSpacing,
            children: [
              ...itemHeading(
                !hidden('name') && certificate.Certificate
                  ? chooseDBTranslation(i18n, certificate.Certificate).certificate
                  : '',
                hidden('date') ? '' : date
              ),
              ...(hidden('certifier')
                ? []
                : [
                    new TextRun({
                      text: `${t('print.certifier')}: `,
                      bold: true,
                      break: 1,
                    }),
                    new TextRun({
                      text: chooseDBTranslation(i18n, certificate).certifier,
                    }),
                  ]),
              ...(showCertificateExpiry && endDate
                ? [
                    new TextRun({
                      text: `${t('print.certificateExpiry')}: `,
                      bold: true,
                      break: 1,
                    }),
                    new TextRun({
                      text: endDate,
                    }),
                  ]
                : []),
              ...(certificate.url && !hidden('url')
                ? [
                    new TextRun({
                      text: `${t('print.url')}: `,
                      bold: true,
                      break: 1,
                    }),
                    new TextRun({
                      text: certificate.url,
                    }),
                  ]
                : []),
              ...(hidden('description')
                ? []
                : [
                    new TextRun({
                      text: chooseDBTranslation(i18n, certificate).description,
                      size: fontSize.small,
                      break: 1,
                    }),
                  ]),
            ],
          })
        )
      }
      break

    case 'competences':
      for (let i = 0; i < data.personCompetences.length; i++) {
        const competence = data.personCompetences[i]
        let date = ''
        if (competence.issueDate) {
          date += `${convertToDisplayDate(competence.issueDate, competence.dateResolution)} - `
        }
        if (competence.endDate) {
          date += `${convertToDisplayDate(competence.endDate, competence.dateResolution)}`
        }

        items.push(
          new Paragraph({
            keepLines: true,
            style: 'CaleoNormal',
            tabStops: itemHeadingTabStops,
            spacing: itemParagraphSpacing,
            children: [
              ...itemHeading(
                !hidden('name') && competence.Competence ? chooseDBTranslation(i18n, competence.Competence).name : '',
                hidden('date') ? '' : date
              ),
              ...(hidden('issuer')
                ? []
                : [
                    new TextRun({
                      text: `${t('print.issuer')}: `,
                      bold: true,
                      break: 1,
                    }),
                    new TextRun({
                      text: chooseDBTranslation(i18n, competence).issuer,
                    }),
                  ]),
              ...(hidden('description')
                ? []
                : [
                    new TextRun({
                      text: chooseDBTranslation(i18n, competence).description,
                      size: fontSize.small,
                      break: 1,
                    }),
                  ]),
            ],
          })
        )
      }
      break
  }

  return items
}

export default education
